/**
 * Pure SQL transforms for the story SQL dialog: the single "Download all" file (client-side, no request).
 */

export interface StorySqlFileEntry {
  title: string;
  /** The statements to save under the title; empty when the chart's SQL could not be read. */
  statements: readonly { output: string | null; sql: string }[];
  /** Why there is no SQL, shown as a comment. */
  message?: string | null;
}

/** One `.sql` text: a comment header per chart, then its statements. */
export const buildAllSqlFile = (storyTitle: string, entries: readonly StorySqlFileEntry[]): string => {
  const oneLine = (text: string) => text.replace(/\s+/g, " ").trim();
  const blocks = entries.map((entry, index) => {
    const head = `-- ${index + 1}. ${oneLine(entry.title)}`;
    if (entry.statements.length === 0) {
      return `${head}\n-- ${oneLine(entry.message ?? "No SQL available")}`;
    }
    const body = entry.statements
      .map((s) => `${s.output ? `-- output: ${oneLine(s.output)}\n` : ""}${s.sql.trim().replace(/;?$/, ";")}`)
      .join("\n\n");
    return `${head}\n${body}`;
  });
  return [`-- ${oneLine(storySqlFileTitle(storyTitle))}`, ...blocks].join("\n\n") + "\n";
};

const storySqlFileTitle = (title: string) => `SQL behind: ${title}`;

/** A safe file name for the download, ending in `.sql`. */
export const sqlFileName = (storyTitle: string): string => {
  const slug = storyTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return `${slug || "story"}-sql.sql`;
};
